import React, { useContext, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { Login } from "./auth/Login";
import { SearchResultsProvider } from "./context/SearchResultsContext";

import Tasks from "./pages/Tasks/Tasks";
import { TaskForm } from "./forms/TaskForm/TaskForm";
import { Labels } from "./pages/Labels/Labels";
import { Notes } from "./pages/Notes/Notes";
import CaseDetails from "./pages/CaseDetails/CaseDetails";
import { Dashboard } from "./pages/Dashboard/Dashboard";
import { ThemeProvider, ThemeContext } from "./context/ThemeContext";
import Settings from "./pages/Settings/Settings";
import { Cases } from "./pages/Cases/Cases";
import { ToastProvider, useToast } from "./context/ToastContext";
import { LoadingProvider } from "./context/LoadingContext";
import gsap from "gsap";
import { NoteForm } from "./forms/NoteForm/NoteForm";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PageNotFound } from "./pages/PageNotFound/PageNotFound";
import { AuthProvider,useAuth } from "./context/AuthContext";
import { AddUsers } from "./pages/AddUsers/AddUsers";
import { UserForm } from "./forms/UserForm/UserForm";
import { Branch } from "./pages/Branch/Branch";
import { BranchDetails } from "./pages/BranchDetails/BranchDetails";
import { MyClients } from "./pages/MyClients/MyClients";
import { MyClientForm } from "./forms/MyClientForm/MyClientForm";
import { Timeline } from "./pages/Timeline/Timeline";
import { Expenses } from "./pages/Expenses/Expenses";
import { SuperAdminModule } from "./pages/SuperAdminModule/SuperAdminModule";
import { SAPlanForm } from "./forms/SAPlanForm/SAPlanForm";
import { SAUserForm } from "./forms/SAUserForm/SAUserForm";
import { SACompanyForm } from "./forms/SACompanyForm/SACompanyForm";
import { Invoices } from "./pages/Invoices/Invoices";
import { DisplayBoard } from "./pages/DisplayBoard/DisplayBoard";
import { GlobalSettingsProvider } from "./context/GlobalSettingsContext";
import UserDetailPage from "./pages/UserDetailsPage/UserDetailsPage";
import PlanDetailsPage from "./pages/PlanDetailsPage/PlanDetailsPage";
import CompanyDetailsPage from "./pages/CompanyDetailsPage/CompanyDetailsPage";
import { SystemInvoiceForm } from "./forms/SystemInvoiceForm/SystemInvoiceForm";
import ExtendedViewPage from "./pages/ExtendedViewPage/ExtendedViewPage";
import Profile from "./pages/Profile/Profile";
import GlobalSettings from "./pages/GlobalSettingsPage/GlobalSettingsPage";
import { TaskTabProvider } from "./context/TaskTabContext";
import { CaseSelectionProvider } from "./context/CaseSelectionContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { ErrorPage } from "./pages/ErrorPage/ErrorPage";
import ViewTask from "./pages/ViewTask/ViewTask";
import ExpenseView from "./pages/ExpenseView/ExpenseView";
import ViewMyClient from "./pages/ViewMyClient/ViewMyClient";
import ViewNote from "./pages/ViewNote/ViewNote";
import { ViewAdditionalDoc } from "./pages/ViewAdditionalDoc/ViewAdditionalDoc";
import { BubbleSettingsProvider } from "./context/BubbleSettingsContext";
import { ViewLabel } from "./pages/ViewLabel/ViewLabel";
import { CaseForm } from "./forms/CaseForm/CaseForm";
import { BranchForm } from "./forms/BranchForm/BranchForm";
import { InvoiceForm } from "./forms/InvoiceForm/InvoiceForm";
import { LabelForm } from "./forms/LabelForm/LabelForm";
import { ExpenseForm } from "./forms/ExpenseForm/ExpenseForm";
import AddEditCourtDetailsForm from "./forms/AddEditCourtDetailsForm/AddEditCourtDetailsForm";
import { TaskSelectionProvider } from "./context/TaskSelectionContext";
import { InvoiceSelectionProvider } from "./context/InvoiceSelectionContext";
import { LabelSelectionProvider } from "./context/LabelSelectionContext";
import { NoteSelectionProvider } from "./context/NoteSelectionContext";
import { ExpenseSelectionProvider } from "./context/ExpenseSelectionContext";
import { UserSelectionProvider } from "./context/UserSelectionContext";
import { ClientSelectionProvider } from "./context/ClientSelectionContext";
import { CaseLimitProvider } from "./context/CaseLimitContext";
import { setAuthHandlers } from "./services/api/axios";


function AppContent() {
  //merge
  const { themeColor } = useContext(ThemeContext);
  const appRef = useRef(null);
  const overlayRef = useRef(null);
  const {logout} =  useAuth();
  const {showToast} = useToast();

    useEffect(() => {
    setAuthHandlers(logout, showToast);
  }, [logout, showToast]);

  useEffect(() => {
    if (appRef.current) {
      gsap.fromTo(
        appRef.current,
        { opacity: 0.7 },
        { opacity: 1, duration: 0.7, ease: "power1.out" }
      );
    }

    if (overlayRef.current) {
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        {
          opacity: 0.25,
          duration: 0.15,
          ease: "power1.inOut",
          onComplete: () =>
            gsap.to(overlayRef.current, {
              opacity: 0,
              duration: 0.7,
              ease: "power1.inOut",
            }),
        }
      );
    }
  }, [themeColor]);

  return (
    <div
      className="app-wrapper"
      style={{ position: "relative", minHeight: "100vh" }}
    >
      <div className="theme-flash-overlay" ref={overlayRef}></div>
      <div className="app" ref={appRef} style={{ minHeight: "100vh" }}>
        <Router>
          <Routes>
            <Route path="/" element={<Login />} />
            {/* <Route path="/login" element={<Login />} /> */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            >
              <Route path="profile" element={<Profile />} />

              <Route path="settings" element={<GlobalSettings />} />

              <Route path="error" element={<ErrorPage />} />
              <Route path="superadmin" element={<SuperAdminModule />} />
              <Route path="superadmin/addplan" element={<SAPlanForm />} />
              <Route path="superadmin/editplan/:id" element={<SAPlanForm />} />
              <Route
                path="superadmin/plandetails/:id"
                element={<PlanDetailsPage />}
              />
              <Route
                path="superadmin/companydetails/:id"
                element={<CompanyDetailsPage />}
              />
              <Route
                path="superadmin/create-system-invoice/:companyId"
                element={<SystemInvoiceForm />}
              />
              <Route path="addusers/:id" element={<UserDetailPage />} />
              <Route path="superadmin/adduser" element={<SAUserForm />} />
              <Route path="superadmin/edituser/:id" element={<SAUserForm />} />
              <Route path="superadmin/addcompany" element={<SACompanyForm />} />
              <Route
                path="superadmin/editcompany/:id"
                element={<SACompanyForm />}
              />
              <Route
                path="superadmin/court-details/:id"
                element={<AddEditCourtDetailsForm />}
              />
            </Route>
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Router>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BubbleSettingsProvider>
        <GlobalSettingsProvider>
          <ToastProvider>
            <ThemeProvider>
              <CaseLimitProvider>
                <SearchResultsProvider>
                  <InvoiceSelectionProvider>
                    <TaskTabProvider>
                      <TaskSelectionProvider>
                        <LabelSelectionProvider>
                          <NoteSelectionProvider>
                            <UserSelectionProvider>
                              <ExpenseSelectionProvider>
                                <ClientSelectionProvider>
                                  <CaseSelectionProvider>
                                    <LoadingProvider>
                                      <ErrorBoundary fallback={<ErrorPage />}>
                                        <AppContent />
                                      </ErrorBoundary>
                                    </LoadingProvider>
                                  </CaseSelectionProvider>
                                </ClientSelectionProvider>
                              </ExpenseSelectionProvider>
                            </UserSelectionProvider>
                          </NoteSelectionProvider>
                        </LabelSelectionProvider>
                      </TaskSelectionProvider>
                    </TaskTabProvider>
                  </InvoiceSelectionProvider>
                </SearchResultsProvider>
              </CaseLimitProvider>
            </ThemeProvider>
          </ToastProvider>
        </GlobalSettingsProvider>
      </BubbleSettingsProvider>
    </AuthProvider>
  );
}

export default App;
